import type { CartLine, MenuItem, ModifierGroup } from './types.ts';

/**
 * Корзина живёт в localStorage под ключом тенанта: на телефоне могут стоять
 * иконки двух разных шаурмечных, и корзины не должны смешиваться.
 */

const cartKey = (slug: string) => `cart:${slug}`;
const favKey = (slug: string) => `favorites:${slug}`;
const lastOrderKey = (slug: string) => `last-order:${slug}`;
const tokenKey = (slug: string) => `order-tokens:${slug}`;

export interface StoredCart {
  location_local_id: string;
  lines: CartLine[];
  updated_at: number;
}

function safeParse<T>(raw: string | null, fallback: T): T {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

export function loadCart(slug: string): StoredCart | null {
  const stored = safeParse<StoredCart | null>(localStorage.getItem(cartKey(slug)), null);
  if (!stored) return null;

  // Корзина старше суток почти наверняка забыта: цены и стоп-лист изменились.
  if (Date.now() - stored.updated_at > 86_400_000) {
    localStorage.removeItem(cartKey(slug));
    return null;
  }

  return stored;
}

export function saveCart(slug: string, cart: StoredCart): void {
  localStorage.setItem(
    cartKey(slug),
    JSON.stringify({ ...cart, updated_at: Date.now() }),
  );
}

export function clearCart(slug: string): void {
  localStorage.removeItem(cartKey(slug));
}

/** Стабильный ключ набора опций: две одинаковые сборки объединяются в одну строку. */
export function selectionKey(itemLocalId: string, optionLocalIds: string[]): string {
  return `${itemLocalId}|${[...optionLocalIds].sort().join(',')}`;
}

export function lineOptionIds(line: CartLine): string[] {
  return line.selected.map((s) => s.option_local_id);
}

/** Опции по умолчанию: ими предзаполняется карточка позиции при открытии. */
export function defaultSelection(groups: ModifierGroup[]): Record<string, string[]> {
  const result: Record<string, string[]> = {};

  for (const group of groups) {
    const defaults = group.options.filter((o) => o.is_default).map((o) => o.local_id);

    if (defaults.length > 0) {
      result[group.local_id] = defaults.slice(0, group.max_select);
    } else if (group.required && group.options.length > 0) {
      // Обязательная группа без явного default: берём первую опцию,
      // чтобы кнопка «Добавить» была доступна сразу.
      result[group.local_id] = [group.options[0].local_id];
    } else {
      result[group.local_id] = [];
    }
  }

  return result;
}

export interface SelectionValidation {
  valid: boolean;
  /** Группы, в которых не хватает обязательного выбора. */
  missing: ModifierGroup[];
}

export function validateSelection(
  groups: ModifierGroup[],
  selection: Record<string, string[]>,
): SelectionValidation {
  const missing = groups.filter((g) => {
    const chosen = selection[g.local_id] ?? [];
    if (g.required && chosen.length < Math.max(1, g.min_select)) return true;
    if (chosen.length > g.max_select) return true;
    return false;
  });

  return { valid: missing.length === 0, missing };
}

/**
 * Локальная оценка цены и нагрузки для мгновенного отклика интерфейса.
 * Итоговые значения всегда приходят с сервера: эта функция только для UI.
 */
export function estimateLine(
  item: MenuItem,
  selection: Record<string, string[]>,
): { unitPrice: number; load: number; selected: CartLine['selected'] } {
  let unitPrice = item.price;
  let load = item.load_units;
  const selected: CartLine['selected'] = [];

  for (const group of item.modifier_groups) {
    const chosen = selection[group.local_id] ?? [];
    for (const optionLocalId of chosen) {
      const option = group.options.find((o) => o.local_id === optionLocalId);
      if (!option) continue;

      unitPrice += option.price_delta;
      load += option.load_delta;
      selected.push({
        group_local_id: group.local_id,
        group_name: group.name,
        option_local_id: option.local_id,
        option_name: option.name,
        price_delta: option.price_delta,
        load_delta: option.load_delta,
      });
    }
  }

  return { unitPrice, load, selected };
}

export function buildLine(
  item: MenuItem,
  selection: Record<string, string[]>,
  quantity: number,
  note?: string,
): CartLine {
  const { unitPrice, load, selected } = estimateLine(item, selection);

  return {
    line_id: selectionKey(
      item.item_local_id,
      selected.map((s) => s.option_local_id),
    ),
    item_local_id: item.item_local_id,
    item_name: item.name,
    image_path: item.image_path,
    quantity,
    note: note?.trim() || undefined,
    selected,
    estimated_unit_price: unitPrice,
    estimated_load: load,
  };
}

/** Складывает строку в корзину, объединяя с идентичной сборкой. */
export function addLine(lines: CartLine[], incoming: CartLine): CartLine[] {
  const existingIdx = lines.findIndex(
    (l) => l.line_id === incoming.line_id && (l.note ?? '') === (incoming.note ?? ''),
  );

  if (existingIdx === -1) return [...lines, incoming];

  const next = [...lines];
  next[existingIdx] = {
    ...next[existingIdx],
    quantity: Math.min(50, next[existingIdx].quantity + incoming.quantity),
  };
  return next;
}

export function updateQuantity(
  lines: CartLine[],
  lineId: string,
  delta: number,
): CartLine[] {
  return lines
    .map((l) =>
      l.line_id === lineId
        ? { ...l, quantity: Math.max(0, Math.min(50, l.quantity + delta)) }
        : l,
    )
    .filter((l) => l.quantity > 0);
}

export function removeLine(lines: CartLine[], lineId: string): CartLine[] {
  return lines.filter((l) => l.line_id !== lineId);
}

export function cartSubtotal(lines: CartLine[]): number {
  return lines.reduce((sum, l) => sum + l.estimated_unit_price * l.quantity, 0);
}

export function cartLoad(lines: CartLine[]): number {
  return lines.reduce((sum, l) => sum + l.estimated_load * l.quantity, 0);
}

export function cartCount(lines: CartLine[]): number {
  return lines.reduce((sum, l) => sum + l.quantity, 0);
}

/** Формат, который принимает RPC create_order. Цены не передаются намеренно. */
export function toOrderPayload(lines: CartLine[]): Array<{
  item_local_id: string;
  quantity: number;
  option_local_ids: string[];
  note?: string;
}> {
  return lines.map((l) => ({
    item_local_id: l.item_local_id,
    quantity: l.quantity,
    option_local_ids: lineOptionIds(l),
    ...(l.note ? { note: l.note } : {}),
  }));
}

// ---------------------------------------------------------------------------
// Избранное и повтор заказа — без регистрации, только на устройстве
// ---------------------------------------------------------------------------

export function loadFavorites(slug: string): string[] {
  return safeParse<string[]>(localStorage.getItem(favKey(slug)), []);
}

export function toggleFavorite(slug: string, itemLocalId: string): string[] {
  const current = loadFavorites(slug);
  const next = current.includes(itemLocalId)
    ? current.filter((id) => id !== itemLocalId)
    : [...current, itemLocalId];

  localStorage.setItem(favKey(slug), JSON.stringify(next));
  return next;
}

/** Сохраняем состав прошлого заказа для кнопки «Повторить». */
export function saveLastOrder(slug: string, lines: CartLine[]): void {
  localStorage.setItem(lastOrderKey(slug), JSON.stringify(lines));
}

export function loadLastOrder(slug: string): CartLine[] {
  return safeParse<CartLine[]>(localStorage.getItem(lastOrderKey(slug)), []);
}

// ---------------------------------------------------------------------------
// Токены заказов
//
// Токен — единственный доступ клиента к своему заказу. Храним локально,
// чтобы «мои заказы» работали без регистрации.
// ---------------------------------------------------------------------------

export interface StoredOrderToken {
  order_id: string;
  order_number: number;
  token: string;
  created_at: number;
}

export function saveOrderToken(slug: string, entry: StoredOrderToken): void {
  const all = loadOrderTokens(slug);
  // Держим последние 10 заказов: больше в истории клиенту не нужно.
  const next = [entry, ...all.filter((e) => e.order_id !== entry.order_id)].slice(0, 10);
  localStorage.setItem(tokenKey(slug), JSON.stringify(next));
}

export function loadOrderTokens(slug: string): StoredOrderToken[] {
  return safeParse<StoredOrderToken[]>(localStorage.getItem(tokenKey(slug)), []);
}

export function latestOrderToken(slug: string): StoredOrderToken | null {
  return loadOrderTokens(slug)[0] ?? null;
}

/**
 * Ключ идемпотентности. Привязан к составу корзины и слоту, поэтому
 * повторная отправка той же формы (двойной тап, retry после таймаута)
 * не создаёт второй заказ, а изменение заказа создаёт новый ключ.
 */
export function buildIdempotencyKey(
  slug: string,
  locationLocalId: string,
  lines: CartLine[],
  slotStartAt: string | null,
  sessionSalt: string,
): string {
  const composition = lines
    .map((l) => `${l.item_local_id}:${l.quantity}:${lineOptionIds(l).sort().join('+')}`)
    .sort()
    .join('|');

  const raw = `${slug}/${locationLocalId}/${composition}/${slotStartAt ?? 'asap'}/${sessionSalt}`;

  // Простой стабильный хэш: ключу не нужна криптостойкость, нужна
  // детерминированность при одинаковом вводе.
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  for (let i = 0; i < raw.length; i++) {
    const ch = raw.charCodeAt(i);
    h1 = ((h1 ^ ch) * 0x01000193) >>> 0;
    h2 = ((h2 + ch) * 0x85ebca6b) >>> 0;
  }

  return `${h1.toString(16).padStart(8, '0')}${h2.toString(16).padStart(8, '0')}${raw.length.toString(16)}`;
}

/**
 * Соль сессии оформления. Создаётся при входе на экран оформления и живёт
 * до успешного заказа: пока она та же, retry идемпотентен.
 */
export function getCheckoutSalt(slug: string): string {
  const key = `checkout-salt:${slug}`;
  let salt = sessionStorage.getItem(key);
  if (!salt) {
    salt = crypto.randomUUID();
    sessionStorage.setItem(key, salt);
  }
  return salt;
}

export function resetCheckoutSalt(slug: string): void {
  sessionStorage.removeItem(`checkout-salt:${slug}`);
}
